import {Injectable} from '@angular/core';
import {HttpService} from '../http-service.service';
import {ApiConst} from '../api-const';
import {Observable, Subject} from 'rxjs';
import {map} from 'rxjs/operators';
import {SearchCriteria} from '../../../model/search-criteria';

/**
 * Service that handle user Custom request
 */
@Injectable()
export class UserSavedRequestService {

  /**
   * Subject that contains that 'emit' the list of saved requests
   */
  public listOfSavedRequest: Subject<any>;

  /**
   * default constructor: instiantiate variables
   * @param {HttpService} httpService - handle httpRequest
   */
  constructor(private httpService: HttpService) {
    this.listOfSavedRequest = new Subject();
  }

  /**
   * Call getSavedRequest (only usefull for the init)
   */
  public callRequestUpdate() {
    this.getSavedRequest();
  }

  /**
   * get the list of previously saved requests
   * @return Observable<any> - request result
   */
  private getSavedRequest(): void {
    this.httpService.$post(ApiConst.getSavedRequests, null).pipe( map(response => {
      this.listOfSavedRequest.next( response );
    }) ).subscribe();
  }

  /**
   * delete a previously saved request using name
   */
  public deleteSavedRequest(requestName: string): Observable<boolean> {
    const dataToSend = JSON.stringify({
      queryName: requestName
    });

    return this.httpService.$post(ApiConst.deleteSavedRequest, dataToSend).pipe( map(response => {
      this.listOfSavedRequest.next(response);
      return response.body.bOK;
    }) );
  }

  /**
   *
   * Process saved request update or save (this is the same for back-office)
   *
   * @param {string} sName - request name
   * @param {boolean} bDepotDay - if the search should be restricted to current day
   * @param {boolean}  bDepotMonthbDepotMonth - if the search should be restricted to current month
   * @param {boolean}  bDepotWeek - if the search should be restricted to current week
   * @param {boolean} bDepotYesterday - if the search should be restricted to previous day
   * @param {boolean} bLimitedDeposit - if the search should be restricted to deposits
   * @param {boolean} bLimitedSent - if the search should be restricted to sent
   * @param {number} iDepotWeekNum - if the search should be restricted to a precise week number
   * @param {number} iDepotMonthNum - if the search should be restricted to a precise month number
   * @param {Date} dateDepotBegin - if the search should be restricted to a certain period, this is the beginning of the period
   * @param {Date}  dateDepotEnd - if the search should be restricted to a certain period, this is the end of the period
   * @param {SearchCriteria[]} objCriterias - list of SearchCriteria filled with values
   * @return Observable<boolean> - true if the request was successfully processed, false else
   */
  public saveNewRequest(sName: string, bDepotDay: boolean, bDepotMonthbDepotMonth: boolean,
                        bDepotWeek: boolean, bDepotYesterday: boolean, bLimitedDeposit: boolean,
                        bLimitedSent: boolean, iDepotWeekNum: number, iDepotMonthNum: number,
                        dateDepotBegin: Date, dateDepotEnd: Date, objCriterias: SearchCriteria[]): Observable<boolean> {

    const objTrackingCriteria = {
      bDepotDay: bDepotDay,
      bDepotMonth: bDepotMonthbDepotMonth,
      bDepotWeek: bDepotWeek,
      bDepotYesterday: bDepotYesterday,
      bLimitedDeposit: bLimitedDeposit,
      bLimitedSent: bLimitedSent,
      objCriteria: this.formatSearchCriteriasForRequest(objCriterias),
    };
    if (iDepotWeekNum) {
      objTrackingCriteria['iDepotWeekNum'] = iDepotWeekNum;
    }
    if (iDepotMonthNum) {
      objTrackingCriteria['iDepotMonthNum'] = iDepotMonthNum;
    }
    if (dateDepotBegin) {
      objTrackingCriteria['dateDepotBegin'] = dateDepotBegin;
    }
    if (dateDepotEnd) {
      objTrackingCriteria['dateDepotEnd'] = dateDepotEnd;
    }

    const dataToSend = JSON.stringify(({
      sName: sName,
      objTrackingCriteria: objTrackingCriteria,
    }));

    return this.httpService.$post(ApiConst.addSavedRequest, dataToSend).pipe( map(response => {
      this.listOfSavedRequest.next(response);
      return response.body.bOK;
    }) );
  }

  public scheduleRequest(sName: string, sMail: string,
                         sParamBatch: string, sExecuteScheduling: string, bMailNoActivity: boolean,
                         bBatch: boolean, ObjTrackingCriteria: any, ObjDataSentRefList: any []): Observable<boolean> {
    const bCsvExtraction = true;

    const dataToSend = JSON.stringify(({
      sName: sName,
      bCsvExtraction: bCsvExtraction,
      sMail: sMail,
      sParamBatch: sParamBatch,
      sExecuteScheduling: sExecuteScheduling,
      bMailNoActivity: bMailNoActivity,
      bBatch: bBatch,
      objTrackingCriteria: ObjTrackingCriteria,
      listDataRef: {listDataSentRef: ObjDataSentRefList}
    }));

    return this.httpService.$post(ApiConst.addSavedRequest, dataToSend).pipe( map(response => {
      this.listOfSavedRequest.next(response);
      return response.body.bOK;
    }) );

  }
  public executeSavedRequest(sName: string, sMail: string,
                         sParamBatch: string, sExecuteScheduling: string, bMailNoActivity: boolean,
                         bBatch: boolean, ObjTrackingCriteria: any, ObjDataSentRefList: any []): Observable<any> {
    const bCsvExtraction = true;

    const dataToSend = JSON.stringify(({
      sName: sName,
      bCsvExtraction: bCsvExtraction,
      sMail: sMail,
      sParamBatch: sParamBatch,
      sExecuteScheduling: sExecuteScheduling,
      bMailNoActivity: bMailNoActivity,
      bBatch: bBatch,
      objTrackingCriteria: ObjTrackingCriteria,
      listDataRef: {listDataSentRef: ObjDataSentRefList}
    }));

    return this.httpService.$post(ApiConst.executeSavedRequest, dataToSend).pipe( map(response => {
      return response;
    }) );

  }

  /**
   * Retrieve all available columns for planification request
   * @return {Observable<any>} - list of columns
   */
  public getColumnsForPlanification(): Observable<any> {

    return this.httpService.$post(ApiConst.getSavedRequestPlanificationColumns, '' ).pipe( map(response => {
      return response.body.listDataSentRef;
    }) );

  }

  /**
   *
   * Rework a list of objects for back-office request
   *
   * @param {SearchCriteria[]} objCriterias - list of SearchCriteria filled with values
   * @return {Array} - reworked items for request
   */
  private formatSearchCriteriasForRequest(objCriterias: SearchCriteria[]): any[] {
    const formatedArrayOfCriterias = [];
    for (const objCriteria of objCriterias) {
      formatedArrayOfCriterias.push({
        bDisplay: objCriteria.bDisplay,
        bRestrictToSuggestedValues: objCriteria.bRestrictToSuggestedValues,
        sCategory: objCriteria.sCategory,
        sComparatorTrackingValue: objCriteria.sComparatorTrackingValue,
        sCriteriaTrackingValue: objCriteria.sCriteriaTrackingValue,
        sHelpI18n: objCriteria.sHelpI18n,
        sLibI18n: objCriteria.sLibI18n,
        sNameI18n: objCriteria.sNameI18n,
        sNameId: objCriteria.sNameId,
        sScope: objCriteria.sScope,
        sStorageName: objCriteria.sStorageName,
        sStorageType: objCriteria.sStorageType,
        sTypeData: objCriteria.sTypeData
      });
    }

    return formatedArrayOfCriterias;
  }
}
